/**
 * Stale chunk auto-reload.
 *
 * Vite produces hashed JS chunks. After a new deploy the old hashes vanish
 * from the CDN, so any lazy `import()` from a still-open tab throws:
 *   - Chrome/Firefox: "Failed to fetch dynamically imported module"
 *   - Safari:         "Importing a module script failed."
 *
 * Strategy: detect that exact error globally, hard-reload the page ONCE
 * (sessionStorage flag prevents infinite reload loops).
 */

const RELOAD_FLAG = '__chunk_reload_attempt__';

const STALE_CHUNK_PATTERNS = [
  /Importing a module script failed/i, // Safari
  /Failed to fetch dynamically imported module/i, // Chrome/Firefox
  /error loading dynamically imported module/i, // Vite
  /Unable to preload CSS/i, // Vite preload
];

export function isStaleChunkError(err: unknown): boolean {
  const msg =
    err instanceof Error
      ? err.message
      : typeof err === 'string'
        ? err
        : ((err as any)?.message ?? '');
  if (!msg) return false;
  return STALE_CHUNK_PATTERNS.some(re => re.test(msg));
}

export function triggerChunkReload(): boolean {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) {
      // Already tried once this session — don't loop.
      return false;
    }
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    /* sessionStorage unavailable — still reload */
  }
  // Cache-bust to make sure we get fresh HTML / chunk manifest.
  const url = new URL(window.location.href);
  url.searchParams.set('_r', Date.now().toString(36));
  window.location.replace(url.toString());
  return true;
}

export function clearChunkReloadFlag() {
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* ignore */
  }
}

export function installChunkReloadHandler() {
  if (typeof window === 'undefined') return;

  // If we reach this point successfully, app booted — clear the flag so
  // a later stale-chunk error can trigger another reload.
  window.addEventListener('load', () => {
    // Give the app a few seconds to actually mount before clearing.
    setTimeout(clearChunkReloadFlag, 5000);
  });

  window.addEventListener('error', event => {
    if (isStaleChunkError(event.error ?? event.message)) {
      triggerChunkReload();
    }
  });

  window.addEventListener('unhandledrejection', event => {
    if (isStaleChunkError(event.reason)) {
      triggerChunkReload();
    }
  });
}
